(() => {
  if (window.__promoPackListInitialized) return;
  window.__promoPackListInitialized = true;

  const ROOT_SELECTOR = ".promo-pack-list";
  const controllers = new WeakMap();
  const formBindings = new Map();
  const variantRequestCache = new Map();
  const VARIANT_FETCH_CONCURRENCY = 6;
  let observerInstalled = false;

  function parseJsonScript(container, selector, fallback) {
    const node = container.querySelector(selector);
    if (!node) return fallback;
    try {
      const raw = (node.textContent || "").trim();
      if (!raw) return fallback;
      return JSON.parse(raw);
    } catch (_error) {
      return fallback;
    }
  }

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "");
  }

  function cleanComponentLabel(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    return raw
      .replace(/\s[—-]\s.+$/, "")
      .trim();
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function normalizeOptionName(value) {
    const normalized = normalizeText(value);
    if (["colour", "couleur", "farbe", "colore"].includes(normalized)) return "color";
    if (["talla", "taille", "groesse", "grosse", "tamano"].includes(normalized)) return "size";
    return normalized;
  }

  function toNumericId(value) {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text) return null;
    if (/^\d+$/.test(text)) return text;
    const parts = text.split("/");
    const last = (parts[parts.length - 1] || "").trim();
    return /^\d+$/.test(last) ? last : null;
  }

  function gidFromId(value) {
    const id = toNumericId(value);
    return id ? `gid://shopify/ProductVariant/${id}` : null;
  }

  function unique(values) {
    return Array.from(new Set(values));
  }

  function escapeCssSelector(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(String(value || ""));
    }
    return String(value || "").replace(/(["\\.#:[\]])/g, "\\$1");
  }

  function getActiveCurrencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR";
  }

  const CURRENCY_SYMBOL_MAP = {
    GBP: "£",
    EUR: "€",
    USD: "$",
  };

  function replaceCurrencyCodeWithSymbol(formatted, currencyCode) {
    const normalizedCode = String(currencyCode || "").toUpperCase();
    const symbol = CURRENCY_SYMBOL_MAP[normalizedCode];
    if (!symbol) {
      return formatted;
    }
    const escapedCode = normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const codePattern = new RegExp(`\\b${escapedCode}\\b`, "g");
    return formatted.replace(codePattern, symbol);
  }

  function formatCurrency(amount, currencyCode) {
    const currency = String(currencyCode || getActiveCurrencyCode() || "EUR").toUpperCase();
    const formatted = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
    return replaceCurrencyCodeWithSymbol(formatted, currency);
  }

  // Mismo formato que el filtro "money" de Liquid (formato de moneda de la tienda), para el precio del botón
  function formatShopMoney(cents) {
    const format =
      (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || "";
    const value = Math.round(Number(cents || 0));
    if (!format) return formatCurrency(value / 100);
    const withDelimiters = (decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split(".");
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const html = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      switch (key) {
        case "amount_no_decimals": return withDelimiters(0, ",", ".");
        case "amount_with_comma_separator": return withDelimiters(2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return withDelimiters(0, ".", ",");
        case "amount_with_apostrophe_separator": return withDelimiters(2, "'", ".");
        case "amount_with_space_separator": return withDelimiters(2, " ", ",");
        default: return withDelimiters(2, ",", ".");
      }
    });
    const holder = document.createElement("div");
    holder.innerHTML = html;
    return holder.textContent;
  }

  function normalizeMarketKeys(rawId) {
    if (!rawId) return [];
    const trimmed = String(rawId).trim();
    if (!trimmed) return [];
    const keys = [trimmed, `market:${trimmed}`];
    const digits = trimmed.match(/(\d+)/)?.[1];
    if (digits) {
      keys.push(digits, `market:${digits}`, `gid://shopify/Market/${digits}`);
    }
    return unique(keys);
  }

  function resolvePriceOverride(variantKey, pricesMap, marketId, countryCode) {
    if (!pricesMap || typeof pricesMap !== "object") return null;
    const numericId = toNumericId(variantKey);
    const gid = String(variantKey).startsWith("gid://shopify/ProductVariant/")
      ? String(variantKey)
      : gidFromId(variantKey);

    const entry =
      (gid && pricesMap[gid]) ||
      (numericId && pricesMap[numericId]) ||
      null;

    if (entry == null) return null;
    if (typeof entry === "number" && Number.isFinite(entry)) return Number(entry);
    if (typeof entry !== "object") return null;

    const candidates = [];
    if (marketId) candidates.push(...normalizeMarketKeys(marketId));
    if (countryCode) candidates.push(`country:${countryCode}`);

    for (const key of candidates) {
      const value = entry[key];
      if (value != null && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }
    return null;
  }

  function extractVariantImage(json) {
    if (!json) return "";
    const featured = json.featured_image;
    if (featured) {
      if (typeof featured === "string") return featured;
      if (featured.src) return String(featured.src);
    }
    if (json.image) {
      if (typeof json.image === "string") return json.image;
      if (json.image.src) return String(json.image.src);
    }
    if (json.featured_media && json.featured_media.preview_image && json.featured_media.preview_image.src) {
      return String(json.featured_media.preview_image.src);
    }
    return "";
  }

  function sizedImageUrl(src, width) {
    const url = String(src || "").trim();
    if (!url) return "";
    if (!/^(https?:)?\/\//.test(url) && !url.startsWith("/")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}width=${width}`;
  }

  async function fetchVariantById(variantId) {
    const id = toNumericId(variantId);
    if (!id) return null;
    if (variantRequestCache.has(id)) return variantRequestCache.get(id);

    // Con la ruta del idioma (/es/…) el nombre del producto llega traducido
    const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const request = fetch(`${localeRoot}variants/${id}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        if (!json) return null;
        const normalizedProductTitle = cleanComponentLabel(
          String(json.product_title || "").trim() || String(json.name || "").trim(),
        );
        return {
          id,
          gid: gidFromId(id),
          title: String(json.title || "").trim(),
          productTitle: normalizedProductTitle,
          available: (json.available ?? json.available_for_sale ?? json.availableForSale ?? false) === true,
          priceCents: Number(json.price || 0),
          compareAtPriceCents: Number(json.compare_at_price || 0),
          options: [json.option1, json.option2, json.option3].filter((value) => value != null && String(value).trim() !== ""),
          imageSrc: extractVariantImage(json),
          productId:
            toNumericId(json.product_id) ||
            (json.featured_image ? toNumericId(json.featured_image.product_id) : null),
        };
      })
      .catch(() => null);

    variantRequestCache.set(id, request);
    return request;
  }

  async function fetchVariantsByIds(ids) {
    const normalized = unique(
      (Array.isArray(ids) ? ids : [])
        .map((id) => toNumericId(id))
        .filter(Boolean),
    );
    if (!normalized.length) return new Map();

    // Big packs allow hundreds of variants; firing every request at once gets
    // throttled and any failed request silently drops that option from the list.
    const map = new Map();
    let cursor = 0;
    const worker = async () => {
      while (cursor < normalized.length) {
        const id = normalized[cursor];
        cursor += 1;
        const row = await fetchVariantById(id);
        if (row && row.id) {
          map.set(row.id, row);
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(VARIANT_FETCH_CONCURRENCY, normalized.length) }, () => worker()),
    );
    return map;
  }

  // Estilo "Tarjetas": opciones (en el idioma de la tienda) y fondo de cada bola de color de un producto del set.
  // /variants/ID redirige a /products/<handle>; con HEAD no se descarga la página. Después se pide la sección
  // promo-pack-swatches, que calcula los colores igual que el selector principal.
  const productOptionsCache = new Map();

  function fetchProductOptions(productId, variantId) {
    const key = String(productId || variantId || "");
    if (!key) return Promise.resolve(null);
    if (productOptionsCache.has(key)) return productOptionsCache.get(key);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const request = fetch(`${root}variants/${toNumericId(variantId)}`, { method: "HEAD" })
      .then((response) => {
        const path = new URL(response.url, window.location.origin).pathname;
        if (!/\/products\//.test(path)) return null;
        return fetch(`${path}?section_id=promo-pack-swatches`).then((res) => (res.ok ? res.text() : ""));
      })
      .then((html) => {
        if (!html) return null;
        const holder = document.createElement("div");
        holder.innerHTML = html;
        const data = parseJsonScript(holder, ".promo-pack-swatches", null);
        if (!data || !Array.isArray(data.options) || !data.options.length) return null;
        data.optionsByVariantId = new Map(
          (data.variants || []).map((variant) => [String(variant.id), variant.options || []]),
        );
        return data;
      })
      .catch(() => null);

    productOptionsCache.set(key, request);
    return request;
  }

  // Foto principal del producto de una variante (para piezas sin foto propia, p. ej. "Default Title")
  const productImageCache = new Map();
  function fetchProductImage(variantId) {
    const id = toNumericId(variantId);
    if (!id) return Promise.resolve("");
    if (productImageCache.has(id)) return productImageCache.get(id);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const request = fetch(`${root}variants/${id}`, { method: "HEAD" })
      .then((response) => {
        const path = new URL(response.url, window.location.origin).pathname;
        if (!/\/products\//.test(path)) return null;
        return fetch(`${path.replace(/\/$/, "")}.js`).then((res) => (res.ok ? res.json() : null));
      })
      .then((json) => (json && (json.featured_image || (json.images && json.images[0]))) || "")
      .catch(() => "");
    productImageCache.set(id, request);
    return request;
  }

  function dedupeRepeatedVariantText(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const parts = raw.split(/\s[-–—]\s/).map((part) => part.trim()).filter(Boolean);
    if (parts.length === 2 && normalizeText(parts[0]) === normalizeText(parts[1])) {
      return parts[0];
    }
    return raw;
  }

  function variantLabel(variant) {
    if (!variant) return "Variant";
    const variantTitle = dedupeRepeatedVariantText(variant.title);
    const productTitle = String(variant.productTitle || "").trim();

    if (!variantTitle || normalizeText(variantTitle) === "defaulttitle") {
      return productTitle || "Variant";
    }

    if (productTitle && normalizeText(variantTitle).startsWith(normalizeText(productTitle))) {
      const stripped = variantTitle.slice(productTitle.length).replace(/^[\s\-–—/]+/, "").trim();
      if (stripped) return stripped;
    }

    return variantTitle;
  }

  function createPackGroupId(packHandle) {
    const base = String(packHandle || "pack")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "pack";
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return `${base}-${window.crypto.randomUUID()}`;
    }
    return `${base}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  class PromoPackController {
    constructor(root) {
      this.root = root;
      this.formId = String(root.dataset.formId || "").trim();
      this.form = this.formId ? document.forms[this.formId] : null;
      this.marketId = String(root.dataset.marketId || "").trim();
      this.countryCode = String(root.dataset.countryCode || "").trim();
      this.showSavings = String(root.dataset.showSavings || "") === "true";
      // Block setting: keep sold-out variants listed (disabled) or hide them.
      this.showOutOfStock = String(root.dataset.showOutOfStock || "true") !== "false";

      this.uiData = parseJsonScript(root, ".promo-pack-list__ui-data", {});
      this.productData = parseJsonScript(root, ".promo-pack-list__product-data", {});
      this.packsData = parseJsonScript(root, ".promo-pack-list__packs-data", []);

      this.optionsEl = root.querySelector("[data-role='options']");
      this.componentsEl = root.querySelector("[data-role='components']");
      this.totalsEl = root.querySelector("[data-role='totals']");
      this.messageEl = root.querySelector("[data-role='message']");
      this.headerSaveEl = root.querySelector("[data-role='header-save']");
      // Estilo "Tarjetas" (como el bloque Opciones de compra): etiqueta con la opción elegida y ahorro dentro de la tarjeta
      this.cardsLayout = root.classList.contains("promo-pack-list--cards");
      this.selectedLabelEl = root.querySelector("[data-role='selected-label']");

      this.singleLabel = this.uiData.singleProductLabel || "Single product";
      this.packUnavailableLabel = this.uiData.packUnavailableLabel || "Unavailable for this variant";
      this.outOfStockLabel = this.uiData.outOfStockLabel || "Sold out";
      this.componentsLabel = this.uiData.componentsLabel || "Choose pack options";
      this.includedLabel = this.uiData.includedLabel || "Included in the pack";
      this.packPriceLabel = this.uiData.packPriceLabel || "Pack price";
      this.regularPriceLabel = this.uiData.regularPriceLabel || "Regular price";
      this.saveLabel = this.uiData.saveLabel || "Save";
      // Prefijo delante del nombre del pack ("Set:"); vacío = solo el nombre
      this.setPrefix = this.uiData.setPrefix == null ? "" : String(this.uiData.setPrefix);
      this.badgeText = this.uiData.badgeText || "";
      this.styleLabel = this.uiData.styleLabel || "Style";
      this.productOptionsById = new Map();
      this.errorLabel = this.uiData.errorLabel || "This pack is not available right now.";
      this.packSubtitleOverrides = this.parseSubtitleOverrides(this.uiData.packSubtitleOverrides);

      this.baseVariantsById = new Map();
      this.baseVariantIdsSet = new Set();
      const productVariants = Array.isArray(this.productData.variants) ? this.productData.variants : [];
      productVariants.forEach((variant) => {
        const id = toNumericId(variant.id);
        if (!id) return;
        const normalized = {
          id,
          gid: gidFromId(id),
          title: String(variant.title || "").trim(),
          available: variant.available === true,
          options: Array.isArray(variant.options) ? variant.options : [],
          priceCents: Number(variant.price || 0),
          compareAtPriceCents: Number(variant.compareAtPrice || 0),
          productTitle: String(this.productData.title || "").trim(),
        };
        this.baseVariantsById.set(id, normalized);
        this.baseVariantIdsSet.add(id);
      });

      this.baseOptionNames = Array.isArray(this.productData.options) ? this.productData.options : [];
      this.state = {
        mode: "single",
        selectedPackId: null,
        componentSelections: {},
      };
      this.packRuntimeById = new Map();
      this.radioName = `promo-pack-${this.formId || "form"}-${Math.random().toString(36).slice(2, 9)}`;
      this.isSubmitting = false;
      this.initialized = false;
      this._galleryPreviewSnapshot = null;
      this._refreshGeneration = 0;
    }

    getCurrentBaseVariantId() {
      const fromForm = this.form?.querySelector("input[name='id']")?.value;
      const normalized = toNumericId(fromForm);
      if (normalized) return normalized;
      const first = this.baseVariantsById.keys().next();
      return first.done ? null : first.value;
    }

    // Color de la variante elegida del producto principal ("" si el producto no tiene opción de color)
    baseColorValue(baseVariant) {
      const index = this.baseOptionNames.findIndex((name) => normalizeOptionName(name) === "color");
      if (index < 0 || !baseVariant) return "";
      return String((baseVariant.options || [])[index] || "").trim();
    }

    getCurrentBaseVariant() {
      const id = this.getCurrentBaseVariantId();
      return id ? this.baseVariantsById.get(id) || null : null;
    }

    parseSubtitleOverrides(rawValue) {
      const map = {};
      const raw = String(rawValue || "").trim();
      if (!raw) return map;

      raw.split(/\r?\n/).forEach((line) => {
        const trimmed = String(line || "").trim();
        if (!trimmed) return;
        const separatorIndex = trimmed.indexOf("|");
        if (separatorIndex <= 0) return;
        const key = trimmed.slice(0, separatorIndex).trim().toLowerCase();
        const subtitle = trimmed.slice(separatorIndex + 1).trim();
        if (!key || !subtitle) return;
        map[key] = subtitle;
      });

      return map;
    }

    resolvePackSubtitle(packProduct) {
      if (!packProduct) return "";
      const handleKey = String(packProduct.handle || "").trim().toLowerCase();
      const idKey = String(packProduct.id || "").trim().toLowerCase();
      const overrideSubtitle =
        this.packSubtitleOverrides[handleKey] ||
        this.packSubtitleOverrides[idKey] ||
        "";
      if (overrideSubtitle) return overrideSubtitle;
      return String(packProduct.subtitle || "").trim();
    }

    getProductGallery() {
      if (!this.formId) return null;
      const escapedFormId = escapeCssSelector(this.formId);
      return document.querySelector(`product-gallery[form="${escapedFormId}"]`);
    }

    getFirstVisibleImageSlide(gallery) {
      if (!gallery) return null;
      const slides = Array.from(gallery.querySelectorAll(".product-gallery__media[data-media-type='image']"));
      return slides.find((slide) => !slide.hidden) || null;
    }

    getFirstVisibleThumbnail(gallery) {
      if (!gallery) return null;
      const thumbs = Array.from(gallery.querySelectorAll(".product-gallery__thumbnail"));
      return thumbs.find((thumb) => !thumb.hidden) || null;
    }

    clearGalleryPreview() {
      const snapshot = this._galleryPreviewSnapshot;
      if (!snapshot) return;

      if (snapshot.slideImg?.isConnected) {
        snapshot.slideImg.src = snapshot.slideSrc || "";
        snapshot.slideImg.srcset = snapshot.slideSrcset || "";
        snapshot.slideImg.alt = snapshot.slideAlt || "";
      }

      if (snapshot.thumbImg?.isConnected) {
        snapshot.thumbImg.src = snapshot.thumbSrc || "";
        snapshot.thumbImg.srcset = snapshot.thumbSrcset || "";
        snapshot.thumbImg.alt = snapshot.thumbAlt || "";
      }

      this._galleryPreviewSnapshot = null;
    }

    // Al elegir el set ya no se cambia la primera foto de la galería por la del pack (la foto del producto del
    // set ya se ve junto a su selector). Solo se limpia por si quedaba aplicada.
    syncGalleryPreview() {
      if (!this.getProductGallery()) return;
      this.clearGalleryPreview();
    }

    findMatchingPackVariant(packProduct, baseVariant) {
      if (!packProduct || !baseVariant) return null;
      const variants = Array.isArray(packProduct.variants) ? packProduct.variants : [];
      const currentBaseVariantId = toNumericId(baseVariant.id);

      // 1) Primary strategy: select the pack variant whose components include the exact
      // currently selected base-product variant ID.
      if (currentBaseVariantId) {
        let firstComponentMatch = null;
        let firstAvailableComponentMatch = null;

        variants.forEach((variant) => {
          const components = this.parseComponents(variant.components);
          const hasCurrentBaseVariant = components.some((component) => {
            const allow = Array.isArray(component?.allow) ? component.allow : [];
            return allow.some((allowId) => toNumericId(allowId) === currentBaseVariantId);
          });
          if (!hasCurrentBaseVariant) return;

          if (!firstComponentMatch) {
            firstComponentMatch = variant;
          }
          if (variant.available === true && !firstAvailableComponentMatch) {
            firstAvailableComponentMatch = variant;
          }
        });

        if (firstAvailableComponentMatch || firstComponentMatch) {
          return firstAvailableComponentMatch || firstComponentMatch;
        }
      }

      // 2) Fallback: option-based matching between product and pack variants.
      const packOptionNames = Array.isArray(packProduct.options) ? packProduct.options : [];
      const baseOptions = Array.isArray(baseVariant.options) ? baseVariant.options : [];

      let availableMatch = null;
      let fallbackMatch = null;

      variants.forEach((variant) => {
        const variantOptions = Array.isArray(variant.options) ? variant.options : [];
        let shared = 0;
        let matched = 0;

        packOptionNames.forEach((packOptionName, packIdx) => {
          const normalizedPackName = normalizeOptionName(packOptionName);
          const baseIdx = this.baseOptionNames.findIndex(
            (baseOptionName) => normalizeOptionName(baseOptionName) === normalizedPackName,
          );
          if (baseIdx === -1) return;
          shared += 1;
          if (normalizeText(variantOptions[packIdx]) === normalizeText(baseOptions[baseIdx])) {
            matched += 1;
          }
        });

        if (shared === 0 || shared !== matched) return;

        if (!fallbackMatch) fallbackMatch = variant;
        if (variant.available === true && !availableMatch) {
          availableMatch = variant;
        }
      });

      return availableMatch || fallbackMatch;
    }

    parseComponents(rawValue) {
      if (Array.isArray(rawValue)) return rawValue;
      if (typeof rawValue === "string") {
        try {
          const parsed = JSON.parse(rawValue);
          return Array.isArray(parsed) ? parsed : [];
        } catch (_error) {
          return [];
        }
      }
      return [];
    }

    findBaseComponentIndex(components) {
      if (!Array.isArray(components)) return -1;
      for (let index = 0; index < components.length; index += 1) {
        const component = components[index];
        const allow = Array.isArray(component?.allow) ? component.allow : [];
        const hasBaseVariant = allow.some((value) => this.baseVariantIdsSet.has(toNumericId(value)));
        if (hasBaseVariant) return index;
      }
      return -1;
    }

    buildPackRuntime(baseVariant) {
      this.packRuntimeById.clear();
      const products = Array.isArray(this.packsData) ? this.packsData : [];
      products.forEach((packProduct) => {
        const packId = String(packProduct.id || "").trim();
        if (!packId) return;
        const matchedVariant = this.findMatchingPackVariant(packProduct, baseVariant);
        if (!matchedVariant) {
          this.packRuntimeById.set(packId, {
            packProduct,
            matchedVariant: null,
            available: false,
            reason: this.packUnavailableLabel,
            components: [],
            baseComponentIndex: -1,
          });
          return;
        }

        const components = this.parseComponents(matchedVariant.components);
        const baseComponentIndex = this.findBaseComponentIndex(components);
        const currentBaseVariantId = toNumericId(baseVariant?.id);
        const baseAllow = baseComponentIndex >= 0
          ? (Array.isArray(components[baseComponentIndex]?.allow) ? components[baseComponentIndex].allow : [])
          : [];
        const containsCurrentBase = baseAllow.some((item) => toNumericId(item) === currentBaseVariantId);
        const available = baseComponentIndex >= 0 && containsCurrentBase;

        this.packRuntimeById.set(packId, {
          packProduct,
          matchedVariant,
          available,
          reason: available ? "" : this.packUnavailableLabel,
          components,
          baseComponentIndex,
        });
      });
    }

    async renderOptions(baseVariant, generation) {
      if (!this.optionsEl) return;
      this.optionsEl.innerHTML = "";
      const optionTotalsByPackId = await this.buildOptionTotalsByPack(baseVariant);

      if (generation !== undefined && generation !== this._refreshGeneration) return;

      if (this.cardsLayout) {
        this.renderCardOptions(baseVariant, optionTotalsByPackId);
        return;
      }

      const singleCard = document.createElement("div");
      singleCard.className = `promo-pack-list__option ${this.state.mode === "single" ? "is-selected" : ""}`;
      singleCard.innerHTML = `
        <label>
          <span class="promo-pack-list__option-main">
            <input type="radio" name="${this.radioName}" value="single" ${this.state.mode === "single" ? "checked" : ""}>
            <span class="promo-pack-list__option-title">${this.singleLabel}</span>
          </span>
        </label>
      `;
      this.optionsEl.appendChild(singleCard);

      const sortedPacks = Array.from(this.packRuntimeById.values());
      sortedPacks.forEach((runtime) => {
        const packId = String(runtime.packProduct.id);
        const checked = this.state.mode === "pack" && this.state.selectedPackId === packId;
        const option = document.createElement("div");
        option.className = `promo-pack-list__option ${checked ? "is-selected" : ""}`;
        option.dataset.packId = packId;
        option.setAttribute("aria-disabled", runtime.available ? "false" : "true");

        const inputDisabled = runtime.available ? "" : "disabled";
        const matched = runtime.matchedVariant;
        const optionTotals = optionTotalsByPackId.get(packId);
        const currency = getActiveCurrencyCode();
        let price = 0;
        let compare = 0;
        if (optionTotals) {
          price = optionTotals.finalTotal;
          compare = optionTotals.compareTotal;
        } else if (matched) {
          price = Number(matched.price || 0) / 100;
          compare = Number(matched.compareAtPrice || 0) / 100;
        }
        const priceText = matched ? formatCurrency(price, currency) : "";
        const compareText =
          matched && compare > price + 0.0001
            ? `<span class="promo-pack-list__option-price-compare">${formatCurrency(compare, currency)}</span>`
            : "";
        const discountPercent =
          matched && compare > price + 0.0001 && compare > 0
            ? Math.round(((compare - price) / compare) * 100)
            : 0;
        const discountText =
          discountPercent > 0
            ? `<span class="promo-pack-list__option-discount">-${discountPercent}%</span>`
            : "";
        const subtitleText = this.resolvePackSubtitle(runtime.packProduct);
        const subtitleHtml = subtitleText
          ? `<span class="promo-pack-list__option-subtitle">${escapeHtml(subtitleText)}</span>`
          : "";
        const unavailableHtml = runtime.available
          ? ""
          : `<span class="promo-pack-list__option-subtitle promo-pack-list__option-subtitle--unavailable">${escapeHtml(runtime.reason)}</span>`;

        option.innerHTML = `
          <label>
            <span class="promo-pack-list__option-main">
              <input type="radio" name="${this.radioName}" value="${packId}" ${checked ? "checked" : ""} ${inputDisabled}>
              <span>
                <span class="promo-pack-list__option-title">${runtime.packProduct.title || "Pack"}</span>
                ${subtitleHtml}
                ${unavailableHtml}
              </span>
            </span>
            <span class="promo-pack-list__option-price">
              ${discountText}
              <span class="promo-pack-list__option-price-current">${priceText}</span>
              ${compareText}
            </span>
          </label>
        `;
        this.optionsEl.appendChild(option);
      });

      this.optionsEl.querySelectorAll(`input[name="${this.radioName}"]`).forEach((input) => {
        input.addEventListener("change", () => {
          if (input.value === "single") {
            this.state.mode = "single";
            this.state.selectedPackId = null;
          } else {
            this.state.mode = "pack";
            this.state.selectedPackId = input.value;
          }
          this.hideMessage();
          this.renderComponents(baseVariant).catch(() => {
            this.showError(this.errorLabel);
          });
          this.syncGalleryPreview();
        });
      });
    }

    async renderComponents(baseVariant, generation) {
      if (!this.componentsEl || !this.totalsEl) return;
      this.componentsEl.innerHTML = "";
      this.totalsEl.innerHTML = "";

      if (this.state.mode !== "pack" || !this.state.selectedPackId) {
        this.syncButtonPrice(baseVariant?.priceCents);
        this.clearHeaderSave();
        this.componentsEl.hidden = true;
        this.totalsEl.hidden = true;
        return;
      }

      const runtime = this.packRuntimeById.get(this.state.selectedPackId);
      if (!runtime || !runtime.available || !runtime.matchedVariant) {
        this.clearHeaderSave();
        this.componentsEl.hidden = true;
        this.totalsEl.hidden = true;
        this.showError(runtime?.reason || this.errorLabel);
        return;
      }

      const components = Array.isArray(runtime.components) ? runtime.components : [];
      if (!components.length) {
        this.clearHeaderSave();
        this.componentsEl.hidden = true;
        this.totalsEl.hidden = true;
        this.showError(this.errorLabel);
        return;
      }

      const baseVariantId = toNumericId(baseVariant?.id);
      if (!baseVariantId) {
        this.clearHeaderSave();
        this.componentsEl.hidden = true;
        this.totalsEl.hidden = true;
        this.showError(this.errorLabel);
        return;
      }

      const selectionByIndex = this.state.componentSelections[this.state.selectedPackId] || {};
      const extraComponents = [];
      const allAllowIds = [];

      components.forEach((component, index) => {
        if (index === runtime.baseComponentIndex) return;
        const allow = Array.isArray(component?.allow) ? component.allow : [];
        if (!allow.length) return;
        // Components with a single allowed variant are still rendered, as a fixed
        // row without selects, so the customer sees everything the pack includes.
        extraComponents.push({ component, index, allow });
        allow.forEach((id) => allAllowIds.push(id));
      });

      const variantDetails = await fetchVariantsByIds(allAllowIds);

      if (this.cardsLayout) {
        const firstVariantByProduct = new Map();
        variantDetails.forEach((detail) => {
          if (detail.productId && !firstVariantByProduct.has(detail.productId)) {
            firstVariantByProduct.set(detail.productId, detail.id);
          }
        });
        await Promise.all(
          Array.from(firstVariantByProduct.entries()).map(([productId, variantId]) =>
            fetchProductOptions(productId, variantId).then((data) => {
              if (data) this.productOptionsById.set(String(productId), data);
            }),
          ),
        );
      }

      if (generation !== undefined && generation !== this._refreshGeneration) return;

      this.componentsEl.hidden = extraComponents.length === 0;
      this.componentsEl.innerHTML = extraComponents.length
        ? `<p class="promo-pack-list__components-label">${this.componentsLabel}</p>`
        : "";

      const nextSelectionByIndex = { ...selectionByIndex };

      // Nada que elegir (cada pieza del pack es una sola variante, p. ej. pack Earth): carrusel "Incluido en el pack:"
      // con la foto y el nombre de cada producto
      const fixedDetails = extraComponents.map((item) =>
        item.allow
          .map((id) => variantDetails.get(toNumericId(id)))
          .filter((detail) => detail && (this.showOutOfStock || detail.available)),
      );
      if (extraComponents.length > 0 && fixedDetails.every((details) => details.length === 1)) {
        this.componentsEl.innerHTML = `<p class="promo-pack-list__components-label">${escapeHtml(this.includedLabel)}:</p>`;
        const track = document.createElement("div");
        track.className = "promo-pack-list__included";
        // Piezas sin foto de variante: la foto principal de su producto
        await Promise.all(
          fixedDetails.map(async (details) => {
            const detail = details[0];
            if (!detail.imageSrc) detail.imageSrc = await fetchProductImage(detail.id);
          }),
        );
        if (generation !== undefined && generation !== this._refreshGeneration) return;
        extraComponents.forEach((item, position) => {
          const detail = fixedDetails[position][0];
          nextSelectionByIndex[item.index] = detail.gid;
          const data = this.productOptionsById.get(String(detail.productId || ""));
          const title = (data && data.title) || detail.productTitle || cleanComponentLabel(item.component?.label) || "";
          const card = document.createElement("div");
          card.className = "promo-pack-list__included-item";
          card.innerHTML = `
            <span class="promo-pack-list__included-thumb">${detail.imageSrc ? `<img src="${escapeHtml(sizedImageUrl(detail.imageSrc, 200))}" alt="" loading="lazy" width="200" height="200">` : ""}</span>
            <span class="promo-pack-list__included-title">${escapeHtml(title)}</span>`;
          track.appendChild(card);
        });
        this.componentsEl.appendChild(track);
        this.state.componentSelections[this.state.selectedPackId] = { ...nextSelectionByIndex };
        await this.renderTotals(baseVariant);
        return;
      }

      for (const item of extraComponents) {
        const row = this.buildComponentRow(item, variantDetails, baseVariant, nextSelectionByIndex);
        if (!row) {
          this.clearHeaderSave();
          this.showError(this.errorLabel);
          this.componentsEl.hidden = true;
          this.totalsEl.hidden = true;
          return;
        }
        this.componentsEl.appendChild(row);
      }

      this.state.componentSelections[this.state.selectedPackId] = { ...nextSelectionByIndex };
      await this.renderTotals(baseVariant);
    }

    buildComponentRow(item, variantDetails, baseVariant, nextSelectionByIndex) {
      const fallbackProductLabel =
        cleanComponentLabel(item.component?.label) || `Option ${item.index + 1}`;

      // Group the allowed variants by their parent product, preserving the
      // order they appear in the pack definition.
      const groupOrder = [];
      const groupByKey = new Map();
      item.allow.forEach((rawId) => {
        const id = toNumericId(rawId);
        if (!id) return;
        const detail = variantDetails.get(id);
        if (!detail) return;
        if (!this.showOutOfStock && !detail.available) return;
        const productTitle =
          cleanComponentLabel(String(detail.productTitle || "").trim()) || fallbackProductLabel;
        const key = detail.productId ? `id:${detail.productId}` : `title:${normalizeText(productTitle)}`;
        let group = groupByKey.get(key);
        if (!group) {
          group = { key, label: productTitle, image: "", variants: [] };
          groupByKey.set(key, group);
          groupOrder.push(group);
        }
        if (!group.image && detail.imageSrc) group.image = detail.imageSrc;
        group.variants.push(detail);
      });

      // Pack con "mismo color que el producto principal" (metacampo custom.pack_match_color del pack): solo las
      // variantes del color elegido arriba; sin la fila de color y solo con los estilos que existen en ese color.
      // Si ninguna variante tiene ese color, se queda el selector normal.
      let lockedColor = null;
      const packRuntime = this.packRuntimeById.get(this.state.selectedPackId);
      const baseColor = packRuntime?.packProduct?.matchColor === true ? this.baseColorValue(baseVariant) : "";
      if (baseColor) {
        const wanted = normalizeText(baseColor);
        const matches = (detail) => {
          const data = this.productOptionsById.get(String(detail.productId || ""));
          const values = (data && data.optionsByVariantId.get(String(detail.id))) || [];
          return values.concat(detail.options || []).some((value) => normalizeText(value) === wanted);
        };
        const filtered = groupOrder
          .map((group) => ({ ...group, variants: group.variants.filter(matches) }))
          .filter((group) => group.variants.length > 0);
        if (filtered.length) {
          groupOrder.splice(0, groupOrder.length, ...filtered);
          groupByKey.clear();
          filtered.forEach((group) => groupByKey.set(group.key, group));
          lockedColor = baseColor;
        }
      }

      const allDetails = groupOrder.reduce((acc, group) => acc.concat(group.variants), []);
      if (!allDetails.length) return null;

      const savedGid = nextSelectionByIndex[item.index];
      let savedDetail = allDetails.find((detail) => detail.gid === savedGid) || null;
      if (!savedDetail && lockedColor && savedGid) {
        // Al cambiar el color del producto principal: mismo producto y misma talla en el color nuevo
        const previous = variantDetails.get(toNumericId(savedGid));
        if (previous) {
          const previousValues = new Set((previous.options || []).map((value) => normalizeText(value)));
          savedDetail =
            allDetails.find((detail) => detail.productId === previous.productId && detail.available &&
              (detail.options || []).some((value) => normalizeText(value) !== normalizeText(lockedColor) && previousValues.has(normalizeText(value)))) ||
            allDetails.find((detail) => detail.productId === previous.productId && detail.available) ||
            null;
        }
      }
      const currentDetail =
        (savedDetail && savedDetail.available ? savedDetail : null) ||
        allDetails.find((detail) => detail.available) ||
        savedDetail ||
        allDetails[0];
      nextSelectionByIndex[item.index] = currentDetail.gid;

      const groupOfDetail = (detail) =>
        groupOrder.find((group) => group.variants.includes(detail)) || groupOrder[0];

      const row = document.createElement("div");
      row.className = "promo-pack-list__component-row";

      const thumbWrap = document.createElement("div");
      thumbWrap.className = "promo-pack-list__component-thumb";
      const img = document.createElement("img");
      img.loading = "lazy";
      img.decoding = "async";
      // Portrait default aspect to limit layout shift before the image loads.
      img.width = 300;
      img.height = 400;
      thumbWrap.appendChild(img);
      row.appendChild(thumbWrap);

      const controls = document.createElement("div");
      controls.className = "promo-pack-list__component-controls";
      row.appendChild(controls);

      // Match the thumbnail height to the controls (both selects stacked) so the
      // image is as tall as the selectors; its width follows the image aspect ratio.
      const syncThumbHeight = () => {
        if (thumbWrap.hidden) return;
        const height = controls.offsetHeight;
        if (height > 0) {
          thumbWrap.style.height = `${height}px`;
        }
      };

      const updateThumb = (detail) => {
        const group = groupOfDetail(detail);
        const src = (detail && detail.imageSrc) || (group && group.image) || "";
        if (src) {
          img.src = sizedImageUrl(src, 240);
          img.alt = (group && group.label) || "";
          thumbWrap.hidden = false;
          syncThumbHeight();
        } else {
          img.removeAttribute("src");
          img.alt = "";
          thumbWrap.hidden = true;
        }
      };

      const variantSelect = document.createElement("select");
      variantSelect.className = "promo-pack-list__variant-select";
      variantSelect.dataset.componentIndex = String(item.index);

      const populateVariants = (group, preferredGid) => {
        variantSelect.innerHTML = "";
        group.variants.forEach((detail) => {
          const option = document.createElement("option");
          option.value = detail.gid;
          option.textContent = detail.available
            ? variantLabel(detail)
            : `${variantLabel(detail)} — ${this.outOfStockLabel}`;
          option.disabled = !detail.available;
          if (!detail.available) option.classList.add("promo-pack-list__option--disabled");
          variantSelect.appendChild(option);
        });
        const preferred = group.variants.find((detail) => detail.gid === preferredGid) || null;
        const target =
          (preferred && preferred.available ? preferred : null) ||
          group.variants.find((detail) => detail.available) ||
          preferred ||
          group.variants[0];
        variantSelect.value = target.gid;
        return target;
      };

      const commitSelection = (detail) => {
        nextSelectionByIndex[item.index] = detail.gid;
        this.state.componentSelections[this.state.selectedPackId] = { ...nextSelectionByIndex };
        updateThumb(detail);
        this.renderTotals(baseVariant).catch(() => {
          this.showError(this.errorLabel);
        });
      };

      const currentGroup = groupOfDetail(currentDetail);

      if (this.cardsLayout && allDetails.length > 1) {
        const picker = this.buildSwatchPicker({ groupOrder, currentDetail, commitSelection, lockedColor });
        if (picker) {
          row.classList.add("promo-pack-list__component-row--swatches");
          controls.appendChild(picker);
          updateThumb(currentDetail);
          // La foto sigue el alto del selector (Style + Color + Talla) aunque cambie al elegir otro producto
          if (typeof ResizeObserver === "function") {
            new ResizeObserver(syncThumbHeight).observe(controls);
          } else if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(() => requestAnimationFrame(syncThumbHeight));
          }
          return row;
        }
      }

      // Only one possible variant: fixed row (image + name) with nothing to choose,
      // so the customer still sees this part of the pack.
      if (allDetails.length === 1) {
        const caption = document.createElement("span");
        caption.className = "promo-pack-list__component-name";
        const variantText = variantLabel(currentDetail);
        const groupLabel = currentGroup.label || "";
        const parts = [];
        if (variantText && groupLabel && normalizeText(variantText).startsWith(normalizeText(groupLabel))) {
          // variantLabel() already falls back to the full product title.
          parts.push(variantText);
        } else {
          if (groupLabel) parts.push(groupLabel);
          if (variantText) parts.push(variantText);
        }
        if (!currentDetail.available) parts.push(this.outOfStockLabel);
        caption.textContent = parts.join(" — ");
        controls.appendChild(caption);
        updateThumb(currentDetail);
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => requestAnimationFrame(syncThumbHeight));
        } else {
          syncThumbHeight();
        }
        return row;
      }

      // When the slot can be filled by more than one product, split the picker:
      // first select chooses the product, second select shows its variants.
      if (groupOrder.length > 1) {
        const productSelect = document.createElement("select");
        productSelect.className = "promo-pack-list__product-select";
        groupOrder.forEach((group) => {
          const option = document.createElement("option");
          option.value = group.key;
          const groupHasStock = group.variants.some((detail) => detail.available);
          option.textContent = groupHasStock
            ? group.label
            : `${group.label} — ${this.outOfStockLabel}`;
          option.disabled = !groupHasStock;
          if (!groupHasStock) option.classList.add("promo-pack-list__option--disabled");
          productSelect.appendChild(option);
        });
        productSelect.value = currentGroup.key;
        controls.appendChild(productSelect);

        productSelect.addEventListener("change", () => {
          const group = groupByKey.get(productSelect.value) || groupOrder[0];
          const chosen = populateVariants(group, null);
          commitSelection(chosen);
        });
      } else {
        const caption = document.createElement("span");
        caption.className = "promo-pack-list__component-name";
        caption.textContent = currentGroup.label;
        controls.appendChild(caption);
      }

      controls.appendChild(variantSelect);
      populateVariants(currentGroup, currentDetail.gid);

      variantSelect.addEventListener("change", () => {
        const detail =
          allDetails.find((entry) => entry.gid === variantSelect.value) || currentDetail;
        commitSelection(detail);
      });

      updateThumb(currentDetail);

      // The row is appended synchronously by the caller, so measuring on the next
      // frame gives the real controls height once the selects have laid out.
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => requestAnimationFrame(syncThumbHeight));
      } else {
        syncThumbHeight();
      }

      return row;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // Estilo "Tarjetas": selector como el del producto (Style → bolas de color → tallas) en vez de los desplegables
    // -----------------------------------------------------------------------------------------------------------------
    buildSwatchPicker({ groupOrder, currentDetail, commitSelection, lockedColor = null }) {
      const optionsOf = (group) => this.productOptionsById.get(String(group.variants[0]?.productId || ""));
      if (groupOrder.some((group) => !optionsOf(group))) return null;

      const valuesOf = (detail) => {
        const data = optionsOf({ variants: [detail] });
        return (data && data.optionsByVariantId.get(String(detail.id))) || detail.options || [];
      };

      const picker = document.createElement("div");
      picker.className = "promo-pack-list__picker";
      let selected = currentDetail;

      const makeOption = (label, value) => {
        const wrap = document.createElement("div");
        wrap.className = "promo-pack-list__picker-option";
        wrap.innerHTML = `
          <div class="variant-picker__option-info">
            <div class="h-stack gap-2">
              <p class="text-subdued">${escapeHtml(label)}:</p>
              <span>${escapeHtml(value)}</span>
            </div>
          </div>
          <div class="promo-pack-list__picker-values"></div>
        `;
        picker.appendChild(wrap);
        return wrap.querySelector(".promo-pack-list__picker-values");
      };

      const makeButton = (container, { text, swatch, white, isColor, isSelected, isDisabled, onPick }) => {
        const button = document.createElement("button");
        button.type = "button";
        if (isColor) {
          button.className = `color-swatch rounded-full ${white ? "ring-inset" : ""}`;
          button.style.setProperty("--swatch-background", swatch || "transparent");
          button.innerHTML = `<span class="sr-only">${escapeHtml(text)}</span>`;
          button.title = text;
        } else {
          button.className = "block-swatch";
          button.textContent = text;
        }
        button.classList.toggle("is-selected", isSelected);
        button.classList.toggle("is-disabled", isDisabled);
        button.setAttribute("aria-pressed", isSelected ? "true" : "false");
        if (isDisabled) button.setAttribute("aria-disabled", "true");
        button.addEventListener("click", () => {
          if (isDisabled || isSelected) return;
          onPick();
        });
        container.appendChild(button);
      };

      // Mejor variante del grupo para unas opciones dadas: en stock y con el mayor número de coincidencias
      const bestVariant = (group, wanted, fixedIndex) => {
        let best = null;
        let bestScore = -1;
        group.variants.forEach((detail) => {
          const values = valuesOf(detail);
          if (fixedIndex != null && values[fixedIndex] !== wanted[fixedIndex]) return;
          let score = detail.available ? 100 : 0;
          wanted.forEach((value, index) => {
            if (values[index] === value) score += index === fixedIndex ? 0 : 10 - index;
          });
          if (score > bestScore) {
            best = detail;
            bestScore = score;
          }
        });
        return best;
      };

      const pick = (detail) => {
        selected = detail;
        commitSelection(detail);
        render();
      };

      const render = () => {
        picker.innerHTML = "";
        const group = groupOrder.find((entry) => entry.variants.includes(selected)) || groupOrder[0];
        const data = optionsOf(group);
        const current = valuesOf(selected);

        // 1. Style: qué producto (solo si el hueco del set admite varios). Si solo hay uno (p. ej. al filtrar por el
        //    color del producto principal), se muestra su nombre para que se vea qué modelo se compra.
        const productName = (data && data.title) || (group && group.label) || "";
        if (groupOrder.length <= 1 && productName) {
          const name = document.createElement("p");
          name.className = "promo-pack-list__picker-product";
          name.textContent = productName;
          picker.appendChild(name);
        }
        if (groupOrder.length > 1) {
          const values = makeOption(this.styleLabel, group.label);
          groupOrder.forEach((entry) => {
            const hasStock = entry.variants.some((detail) => detail.available);
            makeButton(values, {
              text: entry.label,
              isSelected: entry === group,
              isDisabled: !hasStock,
              onPick: () => pick(bestVariant(entry, current, null) || entry.variants[0]),
            });
          });
        }

        // 2. Opciones del producto (color con bolas, todas visibles; talla y demás con pastillas)
        data.options.forEach((option, index) => {
          const present = new Set(group.variants.map((detail) => valuesOf(detail)[index]));
          const optionValues = option.values.filter((value) => present.has(value.name));
          if (optionValues.length === 0) return;
          if (optionValues.length === 1 && normalizeText(optionValues[0].name) === "defaulttitle") return;
          // Color fijado al del producto principal: no se elige
          if (lockedColor && option.isColor) return;

          const values = makeOption(option.name, current[index] || "");
          values.classList.toggle("promo-pack-list__picker-values--colors", option.isColor);

          optionValues.forEach((value) => {
            // Agotado: no hay ninguna variante en stock con este valor y las opciones anteriores elegidas
            const inStock = group.variants.some((detail) => {
              if (!detail.available) return false;
              const values = valuesOf(detail);
              if (values[index] !== value.name) return false;
              for (let previous = 0; previous < index; previous += 1) {
                if (values[previous] !== current[previous]) return false;
              }
              return true;
            });
            makeButton(values, {
              text: value.name,
              swatch: value.swatch,
              white: value.white,
              isColor: option.isColor,
              isSelected: current[index] === value.name,
              isDisabled: !inStock,
              onPick: () => {
                const wanted = current.slice();
                wanted[index] = value.name;
                const next = bestVariant(group, wanted, index);
                if (next) pick(next);
              },
            });
          });
        });
      };

      render();
      return picker;
    }

    async collectResolvedSelection(baseVariant, packId = this.state.selectedPackId, selectionMapOverride = null) {
      const normalizedPackId = String(packId || "");
      const runtime = this.packRuntimeById.get(normalizedPackId);
      if (!runtime || !runtime.available || !runtime.matchedVariant) return null;
      const baseVariantId = toNumericId(baseVariant?.id);
      if (!baseVariantId) return null;

      const components = Array.isArray(runtime.components) ? runtime.components : [];
      if (!components.length) return null;

      const selectionMap = selectionMapOverride || this.state.componentSelections[normalizedPackId] || {};
      const selection = [];
      const requiredAllowIds = [];

      for (let index = 0; index < components.length; index += 1) {
        const allow = Array.isArray(components[index]?.allow) ? components[index].allow : [];
        if (!allow.length) return null;

        if (index === runtime.baseComponentIndex) {
          const baseAllowed = allow.some((item) => toNumericId(item) === baseVariantId);
          if (!baseAllowed) return null;
          const gid = gidFromId(baseVariantId);
          if (!gid) return null;
          selection.push(gid);
          continue;
        }

        allow.forEach((id) => requiredAllowIds.push(id));
        const variantDetails = await fetchVariantsByIds(allow);
        const selectedGid = selectionMap[index];
        // Sold-out variants can be listed (disabled) in the picker, so the cart
        // payload must always resolve to a variant that is actually purchasable.
        const selectedDetail = selectedGid ? variantDetails.get(toNumericId(selectedGid)) : null;
        if (selectedDetail && selectedDetail.available && selectedDetail.gid) {
          selection.push(selectedDetail.gid);
          continue;
        }

        const firstAvailable = allow
          .map((id) => variantDetails.get(toNumericId(id)))
          .find((variant) => variant && variant.available);
        if (!firstAvailable || !firstAvailable.gid) return null;
        selection.push(firstAvailable.gid);
      }

      const variantDetails = await fetchVariantsByIds(requiredAllowIds);
      return { runtime, selection: selection.filter(Boolean), variantDetails };
    }

    async calculateTotals(baseVariant, packId = this.state.selectedPackId, selectionMapOverride = null) {
      const resolved = await this.collectResolvedSelection(baseVariant, packId, selectionMapOverride);
      if (!resolved) return null;

      const { runtime, selection, variantDetails } = resolved;
      const matchedVariant = runtime.matchedVariant;
      const packPriceCents = Number(matchedVariant.price || 0);
      const packCompareCents = Number(matchedVariant.compareAtPrice || 0);
      const discountFactor =
        packCompareCents > packPriceCents && packPriceCents > 0 ? packPriceCents / packCompareCents : 1;
      let pricesMap = matchedVariant.prices || {};
      if (typeof pricesMap === "string") {
        try {
          pricesMap = JSON.parse(pricesMap);
        } catch (_error) {
          pricesMap = {};
        }
      }
      if (!pricesMap || typeof pricesMap !== "object") {
        pricesMap = {};
      }

      const compactPricesById = {};
      let compareTotal = 0;
      let finalTotal = 0;

      selection.forEach((gid) => {
        const id = toNumericId(gid);
        if (!id) return;
        const detail = this.baseVariantsById.get(id) || variantDetails.get(id);
        if (!detail) return;
        const baseCents = detail.compareAtPriceCents > 0 ? detail.compareAtPriceCents : detail.priceCents;
        const baseUnits = Number(baseCents || 0) / 100;
        const override = resolvePriceOverride(gid, pricesMap, this.marketId, this.countryCode);
        const finalUnits = override != null ? Number(override) : Number((baseUnits * discountFactor).toFixed(2));
        compareTotal += baseUnits;
        finalTotal += finalUnits;
        compactPricesById[id] = Number(finalUnits.toFixed(2));
      });

      return {
        runtime,
        selection,
        compareTotal: Number(compareTotal.toFixed(2)),
        finalTotal: Number(finalTotal.toFixed(2)),
        compactPricesById,
      };
    }

    async buildOptionTotalsByPack(baseVariant) {
      const totalsByPackId = new Map();
      const tasks = Array.from(this.packRuntimeById.entries()).map(async ([packId, runtime]) => {
        if (!runtime?.available || !runtime?.matchedVariant) return;
        const persistedSelection = this.state.componentSelections[String(packId)] || {};
        const totals = await this.calculateTotals(baseVariant, packId, persistedSelection);
        if (totals) {
          totalsByPackId.set(String(packId), totals);
        }
      });
      await Promise.all(tasks);
      return totalsByPackId;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // Estilo "Tarjetas": "Artículo suelto" y "Set: <pack>" con el diseño de Opciones de compra
    // -----------------------------------------------------------------------------------------------------------------
    cardPriceParts(packId, totals) {
      const runtime = this.packRuntimeById.get(String(packId));
      const matched = runtime?.matchedVariant;
      let price = 0;
      let compare = 0;
      if (totals) {
        price = totals.finalTotal;
        compare = totals.compareTotal;
      } else if (matched) {
        price = Number(matched.price || 0) / 100;
        compare = Number(matched.compareAtPrice || 0) / 100;
      }
      const currency = getActiveCurrencyCode();
      const hasDiscount = matched && compare > price + 0.0001;
      return {
        priceHtml: matched
          ? `<span class="promo-pack-list__option-price-current">${formatCurrency(price, currency)}</span>${
              hasDiscount ? `<span class="promo-pack-list__option-price-compare">${formatCurrency(compare, currency)}</span>` : ""
            }`
          : "",
        saveText: hasDiscount && this.showSavings ? `${this.saveLabel} ${formatCurrency(compare - price, currency)}` : "",
      };
    }

    // Título de la tarjeta del set: el metacampo del pack "Título del set en PDP" (custom.pdp_set_title) o, si está
    // vacío, "Set: <título del pack>"
    setTitleFor(packProduct) {
      if (packProduct && packProduct.setTitle) return packProduct.setTitle;
      return `${this.setPrefix} ${(packProduct && packProduct.title) || "Pack"}`.trim();
    }

    updateSelectedLabel() {
      if (!this.selectedLabelEl) return;
      if (this.state.mode === "pack" && this.state.selectedPackId) {
        const runtime = this.packRuntimeById.get(String(this.state.selectedPackId));
        this.selectedLabelEl.textContent = this.setTitleFor(runtime?.packProduct);
      } else {
        this.selectedLabelEl.textContent = this.singleLabel;
      }
    }

    renderCardOptions(baseVariant, optionTotalsByPackId) {
      const currency = getActiveCurrencyCode();
      const singleChecked = this.state.mode === "single";
      const singlePrice = baseVariant && Number(baseVariant.priceCents || 0) > 0 ? formatCurrency(Number(baseVariant.priceCents) / 100, currency) : "";

      const singleCard = document.createElement("div");
      singleCard.className = `promo-pack-list__option ${singleChecked ? "is-selected" : ""}`;
      singleCard.innerHTML = `
        <label class="promo-pack-list__card">
          <input class="promo-pack-list__card-input" type="radio" name="${this.radioName}" value="single" ${singleChecked ? "checked" : ""}>
          <span class="promo-pack-list__card-head">
            <span class="promo-pack-list__option-title">${escapeHtml(this.singleLabel)}</span>
          </span>
          <span class="promo-pack-list__option-price"><span class="promo-pack-list__option-price-current">${singlePrice}</span></span>
        </label>
      `;
      this.optionsEl.appendChild(singleCard);

      Array.from(this.packRuntimeById.values()).forEach((runtime) => {
        const packId = String(runtime.packProduct.id);
        const checked = this.state.mode === "pack" && this.state.selectedPackId === packId;
        const parts = this.cardPriceParts(packId, optionTotalsByPackId.get(packId));
        const unavailableHtml = runtime.available
          ? ""
          : `<span class="promo-pack-list__option-subtitle promo-pack-list__option-subtitle--unavailable">${escapeHtml(runtime.reason)}</span>`;

        const option = document.createElement("div");
        option.className = `promo-pack-list__option ${checked ? "is-selected" : ""}`;
        option.dataset.packId = packId;
        option.setAttribute("aria-disabled", runtime.available ? "false" : "true");
        option.innerHTML = `
          ${this.badgeText ? `<span class="promo-pack-list__badge">${escapeHtml(this.badgeText)}</span>` : ""}
          <label class="promo-pack-list__card">
            <input class="promo-pack-list__card-input" type="radio" name="${this.radioName}" value="${packId}" ${checked ? "checked" : ""} ${runtime.available ? "" : "disabled"}>
            <span class="promo-pack-list__card-head">
              <span class="promo-pack-list__option-title">${escapeHtml(this.setTitleFor(runtime.packProduct))}</span>
              <span class="promo-pack-list__option-save" ${parts.saveText ? "" : "hidden"}>${parts.saveText}</span>
            </span>
            <span class="promo-pack-list__option-price">${parts.priceHtml}</span>
            ${unavailableHtml}
          </label>
        `;
        this.optionsEl.appendChild(option);
      });

      this.optionsEl.querySelectorAll(`input[name="${this.radioName}"]`).forEach((input) => {
        input.addEventListener("change", () => {
          if (input.value === "single") {
            this.state.mode = "single";
            this.state.selectedPackId = null;
          } else {
            this.state.mode = "pack";
            this.state.selectedPackId = input.value;
          }
          this.optionsEl.querySelectorAll(".promo-pack-list__option").forEach((card) => {
            card.classList.toggle("is-selected", card.contains(input));
          });
          this.updateSelectedLabel();
          this.hideMessage();
          this.renderComponents(baseVariant).catch(() => {
            this.showError(this.errorLabel);
          });
          this.syncGalleryPreview();
        });
      });

      this.updateSelectedLabel();
    }

    updateOptionPriceDisplay(packId, totals) {
      if (!this.optionsEl || !packId) return;
      const card = this.optionsEl.querySelector(
        `.promo-pack-list__option[data-pack-id="${escapeCssSelector(packId)}"]`,
      );
      if (!card) return;
      const priceWrap = card.querySelector(".promo-pack-list__option-price");
      if (!priceWrap) return;

      if (this.cardsLayout) {
        const parts = this.cardPriceParts(packId, totals);
        priceWrap.innerHTML = parts.priceHtml;
        const save = card.querySelector(".promo-pack-list__option-save");
        if (save) {
          save.textContent = parts.saveText;
          save.hidden = !parts.saveText;
        }
        return;
      }

      const runtime = this.packRuntimeById.get(String(packId));
      const matched = runtime?.matchedVariant;
      let price = 0;
      let compare = 0;
      if (totals) {
        price = totals.finalTotal;
        compare = totals.compareTotal;
      } else if (matched) {
        price = Number(matched.price || 0) / 100;
        compare = Number(matched.compareAtPrice || 0) / 100;
      }

      const currency = getActiveCurrencyCode();
      const priceText = matched ? formatCurrency(price, currency) : "";
      const compareText =
        matched && compare > price + 0.0001
          ? `<span class="promo-pack-list__option-price-compare">${formatCurrency(compare, currency)}</span>`
          : "";
      const discountPercent =
        matched && compare > price + 0.0001 && compare > 0
          ? Math.round(((compare - price) / compare) * 100)
          : 0;
      const discountText =
        discountPercent > 0
          ? `<span class="promo-pack-list__option-discount">-${discountPercent}%</span>`
          : "";

      priceWrap.innerHTML = `
              ${discountText}
              <span class="promo-pack-list__option-price-current">${priceText}</span>
              ${compareText}
            `;
    }

    // Precio del botón "Añadir al carrito" (y la cuota de "paga a plazos"): el del set si está elegido; si no, el del producto
    syncButtonPrice(cents) {
      const scope = this.root.closest(".shopify-section") || document;
      const amount = Number(cents || 0);
      if (!(amount > 0)) return;
      scope.querySelectorAll("[data-atc-price]").forEach((price) => {
        price.textContent = formatShopMoney(amount);
      });
      scope.querySelectorAll("[data-atc-installment]").forEach((installment) => {
        const count = parseInt(installment.getAttribute("data-installments-count"), 10) || 3;
        installment.textContent = formatShopMoney(amount / count);
      });
    }

    async renderTotals(baseVariant) {
      if (!this.totalsEl) return;
      if (this.state.mode !== "pack" || !this.state.selectedPackId) {
        this.syncButtonPrice(baseVariant?.priceCents);
        this.clearHeaderSave();
        this.totalsEl.hidden = true;
        this.totalsEl.innerHTML = "";
        return;
      }

      const totals = await this.calculateTotals(baseVariant);
      if (!totals) {
        this.clearHeaderSave();
        this.totalsEl.hidden = true;
        this.totalsEl.innerHTML = "";
        this.showError(this.errorLabel);
        return;
      }

      this.updateOptionPriceDisplay(this.state.selectedPackId, totals);
      this.syncButtonPrice(Math.round(totals.finalTotal * 100));

      const currency = getActiveCurrencyCode();
      const savingsValue = totals.compareTotal - totals.finalTotal;
      const hasSavings = this.showSavings && savingsValue > 0.0001;

      if (!hasSavings) {
        this.clearHeaderSave();
        this.totalsEl.hidden = true;
        this.totalsEl.innerHTML = "";
        this.hideMessage();
        return;
      }

      if (this.headerSaveEl && !this.cardsLayout) {
        this.headerSaveEl.textContent = `${this.saveLabel} ${formatCurrency(savingsValue, currency)}`;
        this.headerSaveEl.hidden = false;
      }

      this.totalsEl.hidden = true;
      this.totalsEl.innerHTML = "";
      this.hideMessage();
    }

    clearHeaderSave() {
      if (!this.headerSaveEl) return;
      this.headerSaveEl.hidden = true;
      this.headerSaveEl.textContent = "";
    }

    showError(message) {
      if (!this.messageEl) return;
      this.messageEl.textContent = message || this.errorLabel;
      this.messageEl.hidden = false;
    }

    hideMessage() {
      if (!this.messageEl) return;
      this.messageEl.hidden = true;
      this.messageEl.textContent = "";
    }

    async refresh() {
      const generation = ++this._refreshGeneration;

      const baseVariant = this.getCurrentBaseVariant();
      if (!baseVariant || !this.optionsEl) return;

      this.buildPackRuntime(baseVariant);
      const selectedStillValid =
        this.state.selectedPackId &&
        this.packRuntimeById.get(this.state.selectedPackId)?.available === true;
      if (!selectedStillValid) {
        this.state.mode = "single";
        this.state.selectedPackId = null;
      }

      await this.renderOptions(baseVariant, generation);
      if (generation !== this._refreshGeneration) return;

      await this.renderComponents(baseVariant, generation);
      if (generation !== this._refreshGeneration) return;

      this.syncGalleryPreview();
      this.initialized = true;
    }

    shouldInterceptSubmit() {
      return this.state.mode === "pack" && !!this.state.selectedPackId;
    }

    async submitPack() {
      if (this.isSubmitting || !this.form) return;
      this.isSubmitting = true;

      const submitButtons = Array.from(this.form.elements).filter((element) => element?.type === "submit");
      submitButtons.forEach((button) => {
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-busy", "true");
      });

      try {
        const baseVariant = this.getCurrentBaseVariant();
        const totals = await this.calculateTotals(baseVariant);
        if (!totals) {
          this.showError(this.errorLabel);
          return;
        }

        const currency = getActiveCurrencyCode();
        const quantityInput = this.form.querySelector("input[name='quantity']");
        const packsQuantity = Math.max(1, Number(quantityInput?.value || 1) || 1);
        const packHandle = String(totals.runtime.packProduct.handle || "").trim();
        const parentVariantId = toNumericId(totals.runtime.matchedVariant?.id);
        if (!parentVariantId) {
          this.showError(this.errorLabel);
          return;
        }

        const counts = {};
        const orderedIds = [];
        const gidById = {};
        totals.selection.forEach((gid) => {
          const id = toNumericId(gid);
          if (!id) return;
          counts[id] = (counts[id] || 0) + 1;
          orderedIds.push(id);
          if (!gidById[id]) {
            gidById[id] = String(gid).startsWith("gid://") ? String(gid) : gidFromId(id);
          }
        });

        const entries = Object.entries(counts);
        if (!entries.length) {
          this.showError(this.errorLabel);
          return;
        }

        const packGroup = createPackGroupId(packHandle || "pack");
        const compactSelection = JSON.stringify(counts);
        const compactSelectionIds = JSON.stringify(orderedIds);
        const compactPricesRaw = JSON.stringify(totals.compactPricesById || {});

        const items = entries.map(([variantId, perPackQuantity], index) => {
          const totalQuantity = Number(perPackQuantity) * packsQuantity;
          const gid = gidById[variantId] || gidFromId(variantId);
          const properties = {
            _pack: packHandle,
            _pack_group: packGroup,
            _pack_parent_variant_id: parentVariantId,
            _promo_pilot_variant_gid: gid,
            _promo_pilot_currency: currency,
          };
          const unitPrice = totals.compactPricesById?.[variantId];
          if (Number.isFinite(unitPrice)) {
            properties._promo_pilot_unit_price = Number(unitPrice).toFixed(2);
          }
          if (index === 0) {
            properties._promo_pilot_selection = compactSelection;
            properties._promo_pilot_selection_format = "id_count_map_v1";
            properties._promo_pilot_selection_ids = compactSelectionIds;
            properties._promo_pilot_selection_gid_prefix = "gid://shopify/ProductVariant/";
            properties._promo_pilot_prices = compactPricesRaw;
            properties._promo_pilot_pack_compare_total = Number(totals.compareTotal).toFixed(2);
            properties._promo_pilot_pack_final_total = Number(totals.finalTotal).toFixed(2);
          }
          return {
            id: Number(variantId),
            quantity: Number(totalQuantity),
            properties,
          };
        });

        let sectionsToBundle = ["variant-added"];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          }),
        );

        const payload = {
          items,
          sections: sectionsToBundle.join(","),
          sections_url: window.location.pathname,
        };

        const addResponse = await fetch("/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
        const addData = await addResponse.json();

        if (!addResponse.ok) {
          const errorMessage = addData?.description || addData?.message || this.errorLabel;
          this.showError(errorMessage);
          this.form.dispatchEvent(
            new CustomEvent("cart:error", {
              bubbles: true,
              detail: { error: errorMessage },
            }),
          );
          return;
        }

        if (
          window.themeVariables?.settings?.cartType === "page" ||
          window.themeVariables?.settings?.pageType === "cart"
        ) {
          window.location.href = `${window.Shopify?.routes?.root || "/"}cart`;
          return;
        }

        const cart = await (await fetch("/cart.js")).json();
        cart.sections = addData.sections;

        this.form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: addData.items || [addData],
              cart,
            },
          }),
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart,
            },
          }),
        );
      } catch (_error) {
        this.showError(this.errorLabel);
      } finally {
        submitButtons.forEach((button) => {
          button.removeAttribute("disabled");
          button.removeAttribute("aria-busy");
        });
        this.isSubmitting = false;
      }
    }
  }

  function getControllersForFormId(formId) {
    const nodes = Array.from(document.querySelectorAll(ROOT_SELECTOR)).filter((node) => {
      return node instanceof HTMLElement && node.dataset.formId === formId;
    });
    return nodes
      .map((node) => controllers.get(node))
      .filter(Boolean);
  }

  function getActiveControllerForFormId(formId) {
    const matches = getControllersForFormId(formId);
    if (!matches.length) return null;
    return matches[0];
  }

  function ensureFormBinding(formId) {
    if (!formId) return;
    const form = document.forms[formId];
    if (!form) return;
    const existing = formBindings.get(formId);
    if (existing && existing.form === form) return;
    if (existing && existing.form && existing.form !== form) {
      existing.form.removeEventListener("variant:change", existing.onVariantChange);
      existing.form.removeEventListener("submit", existing.onSubmitCapture, true);
      if (existing.idInput) {
        existing.idInput.removeEventListener("change", existing.onVariantChange);
      }
      formBindings.delete(formId);
    }

    const onVariantChange = () => {
      window.requestAnimationFrame(() => {
        const formControllers = getControllersForFormId(formId);
        formControllers.forEach((controller) => {
          controller.refresh().catch(() => {});
        });
      });
    };

    const onSubmitCapture = (event) => {
      const controller = getActiveControllerForFormId(formId);
      if (!controller || !controller.shouldInterceptSubmit()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      controller.submitPack();
    };

    form.addEventListener("variant:change", onVariantChange);
    form.addEventListener("submit", onSubmitCapture, { capture: true });

    const idInput = form.querySelector("input[name='id']");
    if (idInput) {
      idInput.addEventListener("change", onVariantChange);
    }

    formBindings.set(formId, {
      form,
      idInput,
      onVariantChange,
      onSubmitCapture,
    });
  }

  function initContainer(container) {
    if (!(container instanceof HTMLElement)) return;
    if (controllers.has(container)) return;
    const controller = new PromoPackController(container);
    if (!controller.formId || !controller.form) return;
    controllers.set(container, controller);
    ensureFormBinding(controller.formId);
    controller.refresh().catch(() => {
      controller.showError(controller.errorLabel);
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((container) => initContainer(container));
    if (observerInstalled) return;
    observerInstalled = true;
    const observer = new MutationObserver(() => {
      document.querySelectorAll(ROOT_SELECTOR).forEach((container) => initContainer(container));
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
